import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Utensils, Sunset, Heart, Sandwich, Coffee } from "lucide-react";
import diningHero from "@/assets/dining-hero.jpg";
import privateDining from "@/assets/private-dining.jpg";
import firee from "@/assets/firee.jpg";
import outdoorGardenDining from "@/assets/outdoor-garden-dining.jpg";
import lunchbox from "@/assets/lunchbox.jpg";
import teaCoffee from "@/assets/tea-coffee.jpg";
import food1 from "@/assets/food-1.jpg";
import food2 from "@/assets/food-2.jpg";
import food3 from "@/assets/food-3.jpg";
const NewDining = () => {
  const diningExperiences = [{
    icon: Utensils,
    title: "Outdoor Garden Dining",
    description: "Enjoy fresh, flavorful meals surrounded by nature in our peaceful garden setting",
    image: outdoorGardenDining
  }, {
    icon: Sunset,
    title: "Sunset Firepit Evenings",
    description: "Unwind by the fire as the sun sets — perfect for stories, stars, and a glass of wine.",
    image: firee
  }, {
    icon: Heart,
    title: "Private Dining (On Request)",
    description: "Celebrate something special or enjoy a quiet moment — we'll set up a private dining experience just for you.",
    image: privateDining
  }, {
    icon: Sandwich,
    title: "Picnic-Style Lunch Boxes for Safaris",
    description: "Heading out for adventure? Take along our carefully packed, delicious safari lunch boxes.",
    image: lunchbox
  }, {
    icon: Coffee,
    title: "Tea & Coffee Served Anytime",
    description: "Refresh yourself with premium tea and coffee available throughout your stay, anytime you need it.",
    image: teaCoffee
  }];
  return <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: `url(${diningHero})`
    }}>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0 bg-primary/20"></div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold mb-4">
            Dining at Kijani 
          </h1>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-light mb-6 opacity-90">
            Tanzania, Served with Love
          </h2>
          <div className="max-w-2xl mx-auto">
            <h3 className="text-lg md:text-xl font-semibold mb-2">Culinary Excellence</h3>
            <p className="text-base md:text-lg opacity-80">
              Experience diverse flavors crafted by expert chefs using fresh, local ingredients
            </p>
          </div>
        </div>
      </section>

      {/* Dining Experiences */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-foreground mb-6">
              Our Dining Experiences
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              From garden dining to safari lunch boxes, each meal is crafted with care and served with the warmth of Tanzanian hospitality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {diningExperiences.map((experience, index) => <Card key={index} className="overflow-hidden shadow-elegant">
                <div className="relative h-64 md:h-80">
                  <img src={experience.image} alt={experience.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
                    <div className="flex items-center mb-3">
                      <experience.icon className="h-6 w-6 text-white mr-2" />
                      <h3 className="font-heading text-xl md:text-2xl font-bold">
                        {experience.title}
                      </h3>
                    </div>
                    <p className="text-sm md:text-base leading-relaxed opacity-90">
                      {experience.description}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Culinary Gallery */}
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-foreground mb-6">
              Culinary Gallery
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Explore our beautiful dining spaces and exquisite culinary creations that await you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-elegant group">
              <img src={food1} alt="Delicious gourmet dish" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="font-heading text-xl font-bold">Gourmet Cuisine</h3>
              </div>
            </div>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-elegant group">
              <img src={food2} alt="Fresh local ingredients" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="font-heading text-xl font-bold">Fresh & Local</h3>
              </div>
            </div>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-elegant group">
              <img src={food3} alt="Artisan pizza" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="font-heading text-xl font-bold">Wood-Fired Delights</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
            Ready to Experience Our Culinary Delights?
          </h2>
          <p className="text-muted-foreground mb-8 text-lg">
            Book your stay and discover the flavors of Tanzania at Kijani Safari Lodge
          </p>
          <Button size="lg" className="bg-primary hover:bg-primary/90 px-8 py-3" onClick={() => window.location.href = '/book'}>
            Reserve Your Table
          </Button>
        </div>
      </section>
    </div>;
};
export default NewDining;